import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { useAuth } from 'contexts/AuthContext';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import { Card, CommonCss, PageContainer, PageTitle } from 'styles/common.styles';
import {
  Meta,
  Guide,
  ModeTabBar,
  ModeTab,
} from 'components/system/SystemPageChrome.styles';

import { ActionButtonGroup, CollapsibleSearchPanel, FormArea } from 'components/forms';
import { HiGridComponent, hiCol, useHiGridHook } from 'components/grid/hiGrid';
import {
  ActiveGridHeader,
  ActiveGridProvider,
  ActiveGridScope,
  useActiveGrid,
} from 'contexts/ActiveGridContext';
import {
  GridExportFocusScope,
  GridExportProvider,
  useExcelExportAction,
  useRegisterGridExport,
} from 'components/grid/excel';
import CodeLookupDialog from 'components/budget/CodeLookupDialog';
import { showAlert } from 'utils/toastBridge';
import { enrichDeptRows, filterDepts, loadDepts, getCachedDepts } from 'master/deptStore';
import { buildDeptAuthProgramTree, listDeptAuthPrograms } from 'master/deptAuthProgramCatalog';
import {
  DEPT_AUTH_MODULE_OPTIONS,
  DEPT_MENU_LEVEL_OPTIONS,
  DEPT_SCOPE_OPTIONS,
  YN_OPTIONS,
  applyDeptFullImplication,
  buildDeptPermissionMatrix,
  buildDeptScreenTree,
  bulkSetDeptMenuLevel,
  bulkSetDeptScreenTree,
  collectGrantedDeptScreens,
  countGrantedScreensInTree,
  joinDeptNames,
  loadDeptPermissions,
  loadDeptScreenPermissions,
  saveDeptPermissions,
  saveDeptScreenPermissions,
  syncDeptGrantChange,
  syncDeptModuleScope,
  syncDeptScreenParentGrants,
  validateDeptPermissions,
  validateDeptScreenPermissions,
} from 'master/permissionStore';

const DEPT_GRID_ID = 'permission-dept';
const SCREEN_GRID_ID = 'permission-dept-screen';
const PERMISSION_GRID_ID = 'permission-dept-module';
const SEARCH_LABEL_WIDTH = '56px';
const MATRIX_SIZE = DEPT_AUTH_MODULE_OPTIONS.length * DEPT_MENU_LEVEL_OPTIONS.length;

function fingerprintDeptPerms(rows = [], deptUid) {
  return (rows || [])
    .filter((r) => r && r.deptUid === deptUid && r.grantedYn === 'Y' && r.mod !== 'D')
    .map(
      (r) =>
        [
          r.moduleCode,
          r.menuLevel,
          r.scopeType || 'OWN',
          [...(r.deptUids || [])].map(String).sort().join(','),
          String(r.remark || '').trim(),
        ].join('|')
    )
    .sort()
    .join(';');
}

function fingerprintDeptScreens(rows = [], deptUid) {
  return (rows || [])
    .filter((r) => r && r.deptUid === deptUid && r.grantedYn === 'Y' && r.mod !== 'D')
    .map((r) => r.screenId)
    .sort()
    .join(';');
}

function deptEditStatus(baselineFp, currentFp) {
  if (currentFp === baselineFp) return { mod: '' };
  return { mod: baselineFp ? 'U' : 'A' };
}

const Split = styled.div`
  display: flex;
  gap: 8px;
  flex: 1;
  min-height: 0;
`;

const Panel = styled(Card)`
  display: flex;
  flex: ${(p) => p.$flex || 1};
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  padding: 0;
  overflow: hidden;
`;

const GridBody = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;
`;





const RightStack = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;
`;

/**
 * 부서 권한설정
 * - 화면구성: 메뉴 leaf 프로그램 멀티 체크 (접근 가능 화면)
 * - 권한구성: 모듈 × 조회/등록/전체 + 데이터범위 (기존)
 * - 의사결정·승인은 사용자 권한설정에서 관리
 */
function DeptPermissionManageInner() {
  const { user, isMaster } = useAuth();
  const tenantCode = useTenantCode();
  const prefsUserId = useMemo(() => buildPrefsUserId(tenantCode, user), [tenantCode, user]);
  const deptGridRef = useRef(null);
  const screenGridRef = useRef(null);
  const permissionGridRef = useRef(null);
  const selectedDeptUidRef = useRef('');
  const allPermissionsRef = useRef([]);
  const allScreensRef = useRef([]);
  const baselinePermRef = useRef(new Map());
  const baselineScreenRef = useRef(new Map());
  const lookupTargetRef = useRef('');
  const [activeMode, setActiveMode] = useState('screen'); // screen | permission
  const [screenAllOn, setScreenAllOn] = useState(false);
  const [selectedHint, setSelectedHint] = useState(
    '부서를 선택하면 화면구성·권한구성을 설정할 수 있습니다.'
  );
  const [permReadOn, setPermReadOn] = useState(false);
  const [permRegOn, setPermRegOn] = useState(false);
  const [permFullOn, setPermFullOn] = useState(false);
  const [deptLookupOpen, setDeptLookupOpen] = useState(false);
  const { activeGridId } = useActiveGrid();

  const programTree = useMemo(() => buildDeptAuthProgramTree(), []);
  const programCatalog = useMemo(() => listDeptAuthPrograms(), []);
  const programCount = programCatalog.length;

  const screenTreeOptions = useMemo(
    () => ({
      idKey: 'id',
      childrenKey: 'subRows',
      checkboxField: 'grantedYn',
      checkboxTrueValue: 'Y',
      checkboxFalseValue: 'N',
    }),
    []
  );

  const searchDefaults = useMemo(
    () => ({ tenantCode, deptCode: '', deptName: '', useYn: 'Y' }),
    [tenantCode]
  );
  const methods = useForm({ mode: 'onSubmit', defaultValues: searchDefaults });
  const { getValues, setValue } = methods;

  useEffect(() => {
    setValue('tenantCode', tenantCode);
  }, [setValue, tenantCode]);

  const [depts, setDepts] = useState(() =>
    enrichDeptRows(getCachedDepts(tenantCode) || [], tenantCode)
  );
  useEffect(() => {
    let cancelled = false;
    loadDepts(tenantCode)
      .then((rows) => {
        if (!cancelled) setDepts(enrichDeptRows(rows || [], tenantCode));
      })
      .catch(() => {
        if (!cancelled) setDepts([]);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantCode]);
  const activeDepts = useMemo(() => depts.filter((r) => r.useYn !== 'N'), [depts]);

  const rebuildBaseline = useCallback((permRows, screenRows) => {
    const deptUids = new Set([
      ...(permRows || []).map((r) => r.deptUid).filter(Boolean),
      ...(screenRows || []).map((r) => r.deptUid).filter(Boolean),
      ...((deptGridRef.current?.getData?.() || []).map((d) => d.deptUid).filter(Boolean)),
    ]);
    const pMap = new Map();
    const sMap = new Map();
    deptUids.forEach((uid) => {
      pMap.set(uid, fingerprintDeptPerms(permRows, uid));
      sMap.set(uid, fingerprintDeptScreens(screenRows, uid));
    });
    baselinePermRef.current = pMap;
    baselineScreenRef.current = sMap;
  }, []);

  const syncDeptEditMarks = useCallback((deptUids) => {
    const targets = deptUids?.length
      ? deptUids
      : [...new Set((deptGridRef.current?.getData?.() || []).map((r) => r.deptUid).filter(Boolean))];
    if (!targets.length) return;
    const data = deptGridRef.current?.getData?.() || [];
    let changed = false;
    const next = data.map((row) => {
      if (!targets.includes(row.deptUid)) return row;
      const pBase = baselinePermRef.current.get(row.deptUid) ?? '';
      const sBase = baselineScreenRef.current.get(row.deptUid) ?? '';
      const pCur = fingerprintDeptPerms(allPermissionsRef.current, row.deptUid);
      const sCur = fingerprintDeptScreens(allScreensRef.current, row.deptUid);
      const status = deptEditStatus(`${pBase}#${sBase}`, `${pCur}#${sCur}`);
      if (row.mod === status.mod) return row;
      changed = true;
      return { ...row, mod: status.mod };
    });
    if (changed) deptGridRef.current?.setData?.(next);
  }, []);

  const withDisplay = useCallback(
    (row) => ({ ...row, deptNamesDisp: joinDeptNames(row.deptNames) }),
    []
  );

  const commitScreenMatrix = useCallback(
    (rows) => {
      const deptUid = selectedDeptUidRef.current;
      const next = rows || [];
      screenGridRef.current?.setData?.(next);
      const grantedCount = countGrantedScreensInTree(next);
      setScreenAllOn(programCount > 0 && grantedCount >= programCount);
      if (!deptUid) return;
      const granted = collectGrantedDeptScreens(next, deptUid);
      allScreensRef.current = [
        ...allScreensRef.current.filter((r) => r.deptUid !== deptUid),
        ...granted,
      ];
      syncDeptEditMarks([deptUid]);
      const deptRow = (deptGridRef.current?.getData?.() || []).find((r) => r.deptUid === deptUid);
      if (deptRow) {
        setSelectedHint(
          `${deptRow.deptCode || ''} · ${deptRow.deptName || ''} · 화면 ${grantedCount}/${programCount} · 권한구성 ${MATRIX_SIZE}행`
        );
      }
    },
    [programCount, syncDeptEditMarks]
  );

  const commitPermissionMatrix = useCallback(
    (rows) => {
      const deptUid = selectedDeptUidRef.current;
      const next = applyDeptFullImplication(rows || []);
      permissionGridRef.current?.setData?.(next.map(withDisplay));
      const active = next.filter((r) => r.mod !== 'D');
      const modules = DEPT_AUTH_MODULE_OPTIONS.map((m) => m.value);
      const levelAllOn = (level) =>
        modules.every((mc) =>
          active.some((r) => r.moduleCode === mc && r.menuLevel === level && r.grantedYn === 'Y')
        );
      setPermReadOn(levelAllOn('READ'));
      setPermRegOn(levelAllOn('REGISTER'));
      setPermFullOn(levelAllOn('FULL'));
      if (!deptUid) return;
      const granted = next
        .filter((r) => r.grantedYn === 'Y')
        .map((r) => ({ ...r, deptUid, mod: '' }));
      allPermissionsRef.current = [
        ...allPermissionsRef.current.filter((r) => r.deptUid !== deptUid),
        ...granted,
      ];
      syncDeptEditMarks([deptUid]);
    },
    [syncDeptEditMarks, withDisplay]
  );

  const mergeCurrentPanels = useCallback(() => {
    const deptUid = selectedDeptUidRef.current;
    if (!deptUid) return;
    commitScreenMatrix(screenGridRef.current?.getData?.() || []);
    commitPermissionMatrix(permissionGridRef.current?.getData?.() || []);
  }, [commitPermissionMatrix, commitScreenMatrix]);

  const loadRightPanels = useCallback(
    (deptUid) => {
      if (!deptUid) {
        screenGridRef.current?.setData?.([]);
        permissionGridRef.current?.setData?.([]);
        return;
      }
      commitScreenMatrix(buildDeptScreenTree(deptUid, allScreensRef.current, programTree));
      commitPermissionMatrix(buildDeptPermissionMatrix(deptUid, allPermissionsRef.current));
      // 트리 펼침 — setData 반영 후
      setTimeout(() => screenGridRef.current?.expandAll?.(), 0);
    },
    [commitPermissionMatrix, commitScreenMatrix, programTree]
  );

  const selectDept = useCallback(
    (row) => {
      if (!row?.deptUid) return;
      const prevUid = selectedDeptUidRef.current;
      if (prevUid && prevUid !== row.deptUid) {
        mergeCurrentPanels();
      }
      selectedDeptUidRef.current = row.deptUid;
      const grantedScreens = allScreensRef.current.filter(
        (r) => r.deptUid === row.deptUid && r.grantedYn === 'Y'
      ).length;
      setSelectedHint(
        `${row.deptCode || ''} · ${row.deptName || ''} · 화면 ${grantedScreens}/${programCount} · 권한구성 ${MATRIX_SIZE}행`
      );
      loadRightPanels(row.deptUid);
      if (prevUid && prevUid !== row.deptUid) syncDeptEditMarks([prevUid, row.deptUid]);
      else syncDeptEditMarks([row.deptUid]);
    },
    [loadRightPanels, mergeCurrentPanels, programCount, syncDeptEditMarks]
  );

  const handleModeChange = useCallback(
    (mode) => {
      if (mode === activeMode) return;
      mergeCurrentPanels();
      setActiveMode(mode);
      if (selectedDeptUidRef.current) {
        const deptRow = (deptGridRef.current?.getData?.() || []).find(
          (r) => r.deptUid === selectedDeptUidRef.current
        );
        if (deptRow) {
          const grantedScreens = allScreensRef.current.filter(
            (r) => r.deptUid === deptRow.deptUid && r.grantedYn === 'Y'
          ).length;
          setSelectedHint(
            `${deptRow.deptCode || ''} · ${deptRow.deptName || ''} · 화면 ${grantedScreens}/${programCount} · 권한구성 ${MATRIX_SIZE}행`
          );
        }
      }
    },
    [activeMode, mergeCurrentPanels, programCount]
  );

  const handleSearch = useCallback(async () => {
    allPermissionsRef.current = await loadDeptPermissions(tenantCode);
    allScreensRef.current = await loadDeptScreenPermissions(tenantCode);
    const filtered = filterDepts(depts, getValues()).map((r) => ({ ...r, mod: '' }));
    deptGridRef.current?.setData?.(filtered);
    rebuildBaseline(allPermissionsRef.current, allScreensRef.current);
    const selected = filtered.find((r) => r.deptUid === selectedDeptUidRef.current);
    if (selected) {
      selectedDeptUidRef.current = '';
      selectDept(selected);
      return;
    }
    selectedDeptUidRef.current = '';
    setSelectedHint('부서를 선택하면 화면구성·권한구성을 설정할 수 있습니다.');
    screenGridRef.current?.setData?.([]);
    permissionGridRef.current?.setData?.([]);
  }, [depts, getValues, rebuildBaseline, selectDept, tenantCode]);

  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 화면 진입 시 최초 조회
  }, []);

  const deptColumns = useMemo(
    () => [
      hiCol.roTextColumn({ header: '부서코드', field: 'deptCode', size: 100 }),
      hiCol.roTextColumn({ header: '부서명', field: 'deptName', size: 140 }),
      hiCol.roTextColumn({ header: '조직레벨', field: 'orgLevelNm', size: 90, align: 'center' }),
      hiCol.roTextColumn({ header: '부서경로', field: 'pathCode', size: 200 }),
    ],
    []
  );

  const screenColumns = useMemo(
    () => [
      hiCol.roTreeColumn({ header: '메뉴/화면', field: 'menuName', size: 320 }),
      hiCol.roTextColumn({ header: '화면ID', field: 'screenId', size: 140 }),
      hiCol.roTextColumn({ header: '유형', field: 'menuTypeNm', size: 70, align: 'center' }),
    ],
    []
  );

  const openDeptLookup = useCallback((row) => {
    if (!row?.deptPermUid && !row?.id) return;
    if (row.scopeType !== 'PICK') {
      showAlert('데이터범위를 「선택부서」로 지정한 행에서만 대상부서를 선택합니다.', 'warning');
      return;
    }
    lookupTargetRef.current = row.deptPermUid || row.id;
    setDeptLookupOpen(true);
  }, []);

  const grantCol = useMemo(() => {
    const base = hiCol.edCheckboxColumn({ header: '부여', field: 'grantedYn', size: 72 });
    return {
      ...base,
      meta: {
        ...base.meta,
        editable: (row) => !row?.grantLocked,
      },
    };
  }, []);

  const permissionColumns = useMemo(
    () => [
      hiCol.roTextColumn({ header: '모듈', field: 'moduleName', size: 100 }),
      hiCol.roTextColumn({ header: '메뉴구성', field: 'menuLevelNm', size: 90, align: 'center' }),
      grantCol,
      hiCol.edComboColumn({
        header: '데이터범위',
        field: 'scopeType',
        size: 140,
        options: DEPT_SCOPE_OPTIONS,
        required: true,
      }),
      hiCol.edTextColumn({
        header: '선택부서',
        field: 'deptNamesDisp',
        size: 200,
        editable: false,
        showSearch: true,
        searchTitle: '대상부서 선택',
        onSearch: ({ row }) => openDeptLookup(row),
      }),
      hiCol.edTextColumn({ header: '비고', field: 'remark', size: 180 }),
    ],
    [grantCol, openDeptLookup]
  );

  const deptGrid = useHiGridHook({
    gridRef: deptGridRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: false,
    confirmCheck: false,
    defaultRow: {},
  });

  const screenGrid = useHiGridHook({
    gridRef: screenGridRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: true,
    confirmCheck: false,
    defaultRow: {},
    treeOptions: screenTreeOptions,
  });

  const permissionGrid = useHiGridHook({
    gridRef: permissionGridRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: true,
    confirmCheck: false,
    defaultRow: {},
  });

  useEffect(() => {
    deptGrid.onSelectRow = (row) => selectDept(row);
    deptGrid.onRowClicked = ({ data }) => selectDept(data);
  }, [deptGrid, selectDept]);

  useEffect(() => {
    screenGrid.onAfterSaveCell = (rowData, { field }) => {
      if (field !== 'grantedYn') return;
      const rows = screenGridRef.current?.getData?.() || [];
      // HiGrid가 하위 cascade 처리 → 부모 체크 상태만 재동기화
      commitScreenMatrix(syncDeptScreenParentGrants(rows));
    };
  }, [commitScreenMatrix, screenGrid]);

  useEffect(() => {
    permissionGrid.onAfterSaveCell = (rowData, { field }) => {
      const rows = permissionGridRef.current?.getData?.() || [];
      if (field === 'grantedYn') {
        if (rowData?.grantLocked && rowData.grantedYn !== 'Y') {
          showAlert(
            '「전체」가 부여된 모듈의 조회·등록은 자동 포함됩니다. 전체를 먼저 해제하세요.',
            'warning'
          );
          commitPermissionMatrix(rows.map((r) => (r.grantLocked ? { ...r, grantedYn: 'Y' } : r)));
          return;
        }
        commitPermissionMatrix(syncDeptGrantChange(rows, rowData));
        return;
      }
      if (field === 'scopeType') {
        commitPermissionMatrix(
          syncDeptModuleScope(rows, rowData.moduleCode, {
            scopeType: rowData.scopeType,
            ...(rowData.scopeType !== 'PICK'
              ? { deptUids: [], deptCodes: [], deptNames: [], deptNamesDisp: '' }
              : {}),
          })
        );
        return;
      }
      if (field === 'remark') {
        commitPermissionMatrix(rows);
      }
    };
  }, [commitPermissionMatrix, permissionGrid]);

  const applyDeptLookup = useCallback(
    (dept) => {
      const target = lookupTargetRef.current;
      if (!target || !dept?.deptUid) return;
      const rows = permissionGridRef.current?.getData?.() || [];
      const index = rows.findIndex((r) => r.deptPermUid === target || r.id === target);
      if (index < 0) return;
      const row = rows[index];
      const deptUids = [...(row.deptUids || [])];
      const deptCodes = [...(row.deptCodes || [])];
      const deptNames = [...(row.deptNames || [])];
      const found = deptUids.indexOf(dept.deptUid);
      if (found >= 0) {
        deptUids.splice(found, 1);
        deptCodes.splice(found, 1);
        deptNames.splice(found, 1);
      } else {
        deptUids.push(dept.deptUid);
        deptCodes.push(dept.deptCode || '');
        deptNames.push(dept.deptName || '');
      }
      commitPermissionMatrix(
        syncDeptModuleScope(rows, row.moduleCode, {
          scopeType: 'PICK',
          deptUids,
          deptCodes,
          deptNames,
        })
      );
    },
    [commitPermissionMatrix]
  );

  const handleToggleMenuLevel = useCallback(
    (menuLevel) => {
      if (!selectedDeptUidRef.current) {
        showAlert('부서를 먼저 선택하세요.', 'warning');
        return;
      }
      const rows = permissionGridRef.current?.getData?.() || [];
      const modules = DEPT_AUTH_MODULE_OPTIONS.map((m) => m.value);
      const currentlyOn = modules.every((mc) =>
        rows.some((r) => r.moduleCode === mc && r.menuLevel === menuLevel && r.grantedYn === 'Y')
      );
      const nextYn = currentlyOn ? 'N' : 'Y';
      commitPermissionMatrix(bulkSetDeptMenuLevel(rows, menuLevel, nextYn));
      const label = DEPT_MENU_LEVEL_OPTIONS.find((o) => o.value === menuLevel)?.label || menuLevel;
      showAlert(
        nextYn === 'Y'
          ? `전 모듈 「${label}」 권한을 일괄 부여했습니다.`
          : `전 모듈 「${label}」 권한을 일괄 해제했습니다.`,
        'success'
      );
    },
    [commitPermissionMatrix]
  );
  const handleToggleAllScreens = useCallback(() => {
    if (!selectedDeptUidRef.current) {
      showAlert('부서를 먼저 선택하세요.', 'warning');
      return;
    }
    const nextOn = !screenAllOn;
    const rows = screenGridRef.current?.getData?.() || [];
    commitScreenMatrix(bulkSetDeptScreenTree(rows, nextOn ? 'Y' : 'N'));
    showAlert(
      nextOn ? '모든 화면 접근을 허용했습니다.' : '모든 화면 접근을 해제했습니다.',
      'success'
    );
  }, [commitScreenMatrix, screenAllOn]);

  const handleSave = useCallback(async () => {
    mergeCurrentPanels();
    const permRows = allPermissionsRef.current.filter(
      (r) => r.mod !== 'D' && r.grantedYn === 'Y'
    );
    const screenRows = allScreensRef.current.filter(
      (r) => r.mod !== 'D' && r.grantedYn === 'Y'
    );
    const pVal = validateDeptPermissions(permRows, depts);
    if (!pVal.ok) {
      showAlert(pVal.message, 'warning');
      return;
    }
    const sVal = validateDeptScreenPermissions(screenRows, depts, programCatalog);
    if (!sVal.ok) {
      showAlert(sVal.message, 'warning');
      return;
    }
    try {
      allPermissionsRef.current = await saveDeptPermissions(tenantCode, permRows);
      allScreensRef.current = await saveDeptScreenPermissions(tenantCode, screenRows);
      rebuildBaseline(allPermissionsRef.current, allScreensRef.current);
      const deptData = (deptGridRef.current?.getData?.() || []).map((r) => ({ ...r, mod: '' }));
      deptGridRef.current?.setData?.(deptData);
      if (selectedDeptUidRef.current) {
        loadRightPanels(selectedDeptUidRef.current);
        syncDeptEditMarks([selectedDeptUidRef.current]);
      }
      showAlert('부서 화면구성·권한구성을 저장했습니다.', 'success');
    } catch (e) {
      showAlert(e?.message || '저장에 실패했습니다.', 'error');
    }
  }, [
    depts,
    loadRightPanels,
    mergeCurrentPanels,
    programCatalog,
    rebuildBaseline,
    syncDeptEditMarks,
    tenantCode,
  ]);

  const searchFields = useMemo(
    () => [
      [
        {
          name: 'deptCode',
          label: '부서코드',
          type: 'text',
          width: CommonCss.w100,
          labelWidth: SEARCH_LABEL_WIDTH,
        },
        {
          name: 'deptName',
          label: '부서명',
          type: 'text',
          width: CommonCss.w100,
          labelWidth: SEARCH_LABEL_WIDTH,
        },
        {
          name: 'useYn',
          label: '사용여부',
          type: 'select',
          width: CommonCss.w100,
          labelWidth: SEARCH_LABEL_WIDTH,
          options: [{ value: '', label: '전체' }, ...YN_OPTIONS],
        },
      ],
    ],
    []
  );

  const getDeptExportData = useCallback(
    () => deptGridRef.current?.getFilteredData?.() ?? deptGridRef.current?.getData?.() ?? [],
    []
  );
  const getDeptExportColumns = useCallback(
    () => deptGridRef.current?.getDataColumns?.() ?? [],
    []
  );
  const getScreenExportData = useCallback(
    () => screenGridRef.current?.getFilteredData?.() ?? screenGridRef.current?.getData?.() ?? [],
    []
  );
  const getScreenExportColumns = useCallback(
    () => screenGridRef.current?.getDataColumns?.() ?? [],
    []
  );
  const getPermissionExportData = useCallback(
    () =>
      permissionGridRef.current?.getFilteredData?.() ??
      permissionGridRef.current?.getData?.() ??
      [],
    []
  );
  const getPermissionExportColumns = useCallback(
    () => permissionGridRef.current?.getDataColumns?.() ?? [],
    []
  );

  useRegisterGridExport({
    id: DEPT_GRID_ID,
    label: '부서',
    getData: getDeptExportData,
    getDataColumns: getDeptExportColumns,
    joinKeys: ['deptUid'],
    order: 0,
  });
  useRegisterGridExport({
    id: SCREEN_GRID_ID,
    label: '화면구성',
    getData: getScreenExportData,
    getDataColumns: getScreenExportColumns,
    joinKeys: ['deptUid', 'screenId'],
    order: 1,
  });
  useRegisterGridExport({
    id: PERMISSION_GRID_ID,
    label: '권한구성',
    getData: getPermissionExportData,
    getDataColumns: getPermissionExportColumns,
    joinKeys: ['deptUid', 'moduleCode', 'menuLevel'],
    order: 2,
  });

  const { openDialog: openExcelDialog, dialog: excelDialog } = useExcelExportAction({
    fileName: '부서권한설정',
  });

  const actionButtons = useMemo(
    () => [
      { id: 'search', label: '조회', icon: '🔍', onClick: handleSearch, hotkey: 'F7' },
      {
        id: 'save',
        label: '저장',
        icon: '✅',
        onClick: handleSave,
        hotkey: 'F10',
        variant: 'success',
      },
      { id: 'excel', label: '엑셀', icon: '📥', onClick: openExcelDialog, hotkey: 'F11' },
    ],
    [handleSave, handleSearch, openExcelDialog]
  );

  const extraButtons = useMemo(() => {
    if (activeMode === 'screen') {
      return [
        {
          id: 'screen-toggle-all',
          label: screenAllOn ? '화면 전체 OFF' : '화면 전체 ON',
          icon: screenAllOn ? '☐' : '☑',
          onClick: handleToggleAllScreens,
          title: screenAllOn
            ? '모든 프로그램 접근 해제'
            : '모든 프로그램 접근 허용',
          variant: screenAllOn ? 'danger' : undefined,
        },
      ];
    }
    return [
      {
        id: 'toggle-read',
        label: '조회 전체',
        icon: permReadOn ? '☑' : '☐',
        onClick: () => handleToggleMenuLevel('READ'),
        title: permReadOn ? '전 모듈 조회 해제' : '전 모듈 조회 부여',
      },
      {
        id: 'toggle-reg',
        label: '등록 전체',
        icon: permRegOn ? '☑' : '☐',
        onClick: () => handleToggleMenuLevel('REGISTER'),
        title: permRegOn ? '전 모듈 등록 해제' : '전 모듈 등록 부여',
      },
      {
        id: 'toggle-full',
        label: '권한 전체',
        icon: permFullOn ? '☑' : '☐',
        onClick: () => handleToggleMenuLevel('FULL'),
        title: permFullOn ? '전 모듈 전체권한 해제' : '전 모듈 전체권한 부여',
      },
    ];
  }, [
    activeMode,
    handleToggleAllScreens,
    handleToggleMenuLevel,
    permFullOn,
    permReadOn,
    permRegOn,
    screenAllOn,
  ]);

  const lookupColumns = useMemo(
    () => [
      hiCol.roTextColumn({ header: '부서코드', field: 'deptCode', size: 110 }),
      hiCol.roTextColumn({ header: '부서명', field: 'deptName', size: 160 }),
      hiCol.roTextColumn({ header: '경로', field: 'pathCode', size: 200 }),
    ],
    []
  );
  const lookupRows = useMemo(
    () => activeDepts.map((r) => ({ ...r, id: r.deptUid })),
    [activeDepts]
  );

  if (!isMaster) {
    return (
      <PageContainer>
        <PageTitle>부서 권한설정</PageTitle>
        <Meta>기관 관리자 전용 화면입니다. 접근 권한이 없습니다.</Meta>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <ActionButtonGroup buttons={actionButtons} extraButtons={extraButtons} />
        <CollapsibleSearchPanel
          storageKey="master-dept-permission"
          prefsUserId={prefsUserId}
          summary="부서 조회"
        >
          <FormArea
            fields={searchFields}
            compact
            onFieldEnter={handleSearch}
            defaultValues={searchDefaults}
          />
        </CollapsibleSearchPanel>
      </FormProvider>

      <Meta>
        ① 화면구성에서 접근 프로그램 선택 → ② 권한구성에서 조회·등록·전체 및 데이터범위 설정
        {' · '}프로그램 {programCount}개 · 의사결정·승인은 사용자 권한설정
      </Meta>

      <Split>
        <Panel $flex={1.15}>
          <ActiveGridHeader gridId={DEPT_GRID_ID} extra="변경 시 좌측 A(신규)/U(수정)">
            부서
          </ActiveGridHeader>
          <GridBody>
            <ActiveGridScope
              gridId={DEPT_GRID_ID}
              label="부서"
              order={0}
              canAdd={false}
              canDelete={false}
              style={{ flex: 1, minHeight: 0, height: '100%' }}
            >
              <GridExportFocusScope
                gridId={DEPT_GRID_ID}
                style={{ flex: 1, minHeight: 0, height: '100%' }}
              >
                <HiGridComponent
                  ref={deptGridRef}
                  columns={deptColumns}
                  height="100%"
                  showRowNumber
                  rowNumberSize={56}
                  showModColumn
                  headerActive={activeGridId === DEPT_GRID_ID}
                  enableColumnFilters
                  enableHeaderMenu
                  enableSelectionStats
                  enableFreeze
                  enableQuickExport
                  exportFileName="부서"
                  gridId={DEPT_GRID_ID}
                  prefsUserId={prefsUserId}
                />
              </GridExportFocusScope>
            </ActiveGridScope>
          </GridBody>
        </Panel>

        <Panel $flex={1.4}>
          <ModeTabBar>
            <ModeTab
              type="button"
              $active={activeMode === 'screen'}
              onClick={() => handleModeChange('screen')}
            >
              화면구성
            </ModeTab>
            <ModeTab
              type="button"
              $active={activeMode === 'permission'}
              onClick={() => handleModeChange('permission')}
            >
              권한구성
            </ModeTab>
          </ModeTabBar>

          <RightStack>
            <Meta style={{ padding: '4px 12px' }}>{selectedHint}</Meta>

            <div
              style={{
                display: activeMode === 'screen' ? 'flex' : 'none',
                flex: 1,
                minHeight: 0,
                flexDirection: 'column',
              }}
            >
              <ActiveGridHeader
                gridId={SCREEN_GRID_ID}
                extra="트리 체크 · 상위 선택 시 하위 일괄 · F10 저장"
              >
                화면구성
              </ActiveGridHeader>
              <GridBody>
                <ActiveGridScope
                  gridId={SCREEN_GRID_ID}
                  label="화면구성"
                  order={1}
                  canAdd={false}
                  canDelete={false}
                  style={{ flex: 1, minHeight: 0, height: '100%' }}
                >
                  <GridExportFocusScope
                    gridId={SCREEN_GRID_ID}
                    style={{ flex: 1, minHeight: 0, height: '100%' }}
                  >
                    <HiGridComponent
                      ref={screenGridRef}
                      columns={screenColumns}
                      height="100%"
                      showRowNumber
                      rowNumberSize={56}
                      headerActive={activeGridId === SCREEN_GRID_ID}
                      treeOptions={screenTreeOptions}
                      {...screenGrid.contextMenuProps}
                      enableColumnFilters
                      enableHeaderMenu
                      enableSelectionStats
                      enableFreeze
                      enableQuickExport
                      exportFileName="화면구성"
                      gridId={SCREEN_GRID_ID}
                      prefsUserId={prefsUserId}
                      colOptions={screenGrid.colOptions}
                    />
                  </GridExportFocusScope>
                </ActiveGridScope>
              </GridBody>
              <Guide>
                메뉴 트리에서 상위(모듈·폴더)를 체크하면 하위 화면이 모두 선택됩니다. 예: 사업 →
                사업구조화관리·사업등록. 전체는 상단 토글, 예외 화면만 개별 해제하면 됩니다.
              </Guide>
            </div>

            <div
              style={{
                display: activeMode === 'permission' ? 'flex' : 'none',
                flex: 1,
                minHeight: 0,
                flexDirection: 'column',
              }}
            >
              <ActiveGridHeader
                gridId={PERMISSION_GRID_ID}
                extra="부여 체크 즉시 반영 · F10 저장"
              >
                권한구성
              </ActiveGridHeader>
              <GridBody>
                <ActiveGridScope
                  gridId={PERMISSION_GRID_ID}
                  label="권한구성"
                  order={2}
                  canAdd={false}
                  canDelete={false}
                  style={{ flex: 1, minHeight: 0, height: '100%' }}
                >
                  <GridExportFocusScope
                    gridId={PERMISSION_GRID_ID}
                    style={{ flex: 1, minHeight: 0, height: '100%' }}
                  >
                    <HiGridComponent
                      ref={permissionGridRef}
                      columns={permissionColumns}
                      height="100%"
                      showRowNumber
                      rowNumberSize={56}
                      headerActive={activeGridId === PERMISSION_GRID_ID}
                      {...permissionGrid.contextMenuProps}
                      enableColumnFilters
                      enableHeaderMenu
                      enableSelectionStats
                      enableFreeze
                      enableQuickExport
                      exportFileName="권한구성"
                      gridId={PERMISSION_GRID_ID}
                      prefsUserId={prefsUserId}
                      colOptions={permissionGrid.colOptions}
                    />
                  </GridExportFocusScope>
                </ActiveGridScope>
              </GridBody>
              <Guide>
                선택한 화면에 대해 데이터 접근 수준(조회·등록·전체)과 데이터범위를 지정합니다.
                전체 체크 시 조회·등록이 자동 포함됩니다.
              </Guide>
            </div>
          </RightStack>
        </Panel>
      </Split>

      <CodeLookupDialog
        open={deptLookupOpen}
        title="데이터범위 대상부서 선택"
        rows={lookupRows}
        columns={lookupColumns}
        enableSearch
        searchConfig={{
          codeFields: ['deptCode'],
          nameFields: ['deptName'],
          codeLabel: '부서코드',
          nameLabel: '부서명',
        }}
        onSelect={applyDeptLookup}
        onClose={() => setDeptLookupOpen(false)}
      />
      {excelDialog}
    </PageContainer>
  );
}

export default function DeptPermissionManage() {
  return (
    <GridExportProvider>
      <ActiveGridProvider>
        <DeptPermissionManageInner />
      </ActiveGridProvider>
    </GridExportProvider>
  );
}
